import { createContext, useContext, useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { T } from "../i18n/translations";
import { PRODUCTS, IMAGES, hydrateProducts, FREE_SHIPPING_THRESHOLD, SHIPPING_COST } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const StoreContext = createContext(null);

// overrides: flat map "hero.title" / "manifesto.chapters.0.text" -> string, applied over the static dictionary
const applyOverrides = (base, flat) => {
  const keys = Object.keys(flat || {});
  if (!keys.length) return base;
  const clone = JSON.parse(JSON.stringify(base, (k, v) => (typeof v === "function" ? undefined : v)));
  const restoreFns = (src, dst) => { for (const k in src) { if (typeof src[k] === "function") dst[k] = src[k]; else if (src[k] && typeof src[k] === "object" && dst[k]) restoreFns(src[k], dst[k]); } };
  restoreFns(base, clone);
  for (const path of keys) {
    const parts = path.split("."); let cur = clone;
    for (let i = 0; i < parts.length - 1; i++) { if (cur[parts[i]] === undefined) cur[parts[i]] = {}; cur = cur[parts[i]]; }
    if (flat[path] !== "" && flat[path] != null) cur[parts[parts.length - 1]] = flat[path];
  }
  return clone;
};

const readJson = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
};
const initialLang = () => {
  const saved = localStorage.getItem("nura_lang");
  if (saved === "fr" || saved === "en") return saved;
  return (navigator.language || "fr").toLowerCase().startsWith("en") ? "en" : "fr";
};
const sessionVisitorId = (() => { let v = sessionStorage.getItem("nura_svid"); if (!v) { v = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2)); sessionStorage.setItem("nura_svid", v); } return v; })();
const persistentVisitorId = (() => { let v = localStorage.getItem("nura_vid"); if (!v) { v = crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2); localStorage.setItem("nura_vid", v); } return v; })();

export const StoreProvider = ({ children }) => {
  const location = useLocation();
  const [lang, setLangState] = useState(initialLang);
  const [cart, setCart] = useState(() => readJson("nura_cart", []).filter((i) => i.qty > 0));
  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [promo, setPromoState] = useState(() => localStorage.getItem("nura_promo") || null);
  const [promoInfo, setPromoInfo] = useState(() => readJson("nura_promo_info", null));
  const [stock, setStock] = useState(null);
  const [config, setConfig] = useState({ shipping_cost: SHIPPING_COST, free_shipping_threshold: FREE_SHIPPING_THRESHOLD, promo_enabled: true, announcement_enabled: false, announcement: { fr: "", en: "" }, cookie_banner_enabled: true });
  const [pages, setPages] = useState([]);
  const [overrides, setOverrides] = useState({ fr: {}, en: {}, images: {} });
  // consent is per visit (sessionStorage): the banner is shown again at every new session
  const [consent, setConsentState] = useState(() => { try { return JSON.parse(sessionStorage.getItem("nura_consent")) || null; } catch { return null; } });

  const loadStock = useCallback(() => axios.get(`${API}/products`).then((r) => {
    hydrateProducts(r.data); setProducts(r.data); setProductsLoaded(true);
    setStock(Object.fromEntries(r.data.map((p) => [p.id, p.buyable ? Math.max(p.stock, p.effective_status === "preorder" ? 999 : 0) : 0])));
    const ok = new Set(r.data.filter((p) => p.buyable).map((p) => p.id));
    setCart((c) => c.filter((i) => ok.has(i.id)));
  }).catch(() => setProductsLoaded(true)), []);
  const loadConfig = useCallback(() => axios.get(`${API}/config`).then((r) => setConfig((c) => ({ ...c, ...r.data }))).catch(() => {}), []);

  // catalogue: on mount, every 60 s and when the tab regains focus
  useEffect(() => {
    loadStock();
    const onFocus = () => document.visibilityState === "visible" && loadStock();
    document.addEventListener("visibilitychange", onFocus);
    const iv = setInterval(loadStock, 60000);
    return () => { document.removeEventListener("visibilitychange", onFocus); clearInterval(iv); };
  }, [loadStock]);
  // shop config (site mode, shipping, announcement…): on mount, every 30 s and on focus
  useEffect(() => {
    loadConfig();
    const onFocus = () => document.visibilityState === "visible" && loadConfig();
    document.addEventListener("visibilitychange", onFocus);
    const iv = setInterval(loadConfig, 30000);
    return () => { document.removeEventListener("visibilitychange", onFocus); clearInterval(iv); };
  }, [loadConfig]);
  // CMS pages and text/image overrides: once
  useEffect(() => {
    axios.get(`${API}/pages`).then((r) => setPages(r.data)).catch(() => {});
    axios.get(`${API}/site-content`).then((r) => setOverrides({ fr: {}, en: {}, images: {}, ...r.data })).catch(() => {});
  }, []);
  useEffect(() => {
    const seo = config.seo || {};
    if (seo.title?.[lang]) document.title = seo.title[lang];
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    if (seo.description?.[lang]) meta.content = seo.description[lang];
    const setProp = (prop, val) => { if (!val) return; let m = document.querySelector(`meta[property="${prop}"]`); if (!m) { m = document.createElement("meta"); m.setAttribute("property", prop); document.head.appendChild(m); } m.content = val; };
    setProp("og:title", seo.title?.[lang]); setProp("og:description", seo.description?.[lang]); setProp("og:url", window.location.origin + window.location.pathname); setProp("og:image", `${window.location.origin}/og-image.jpg`); setProp("og:locale", lang === "fr" ? "fr_CH" : "en_GB");
    if (config.google_site_verification) {
      let gv = document.querySelector('meta[name="google-site-verification"]');
      if (!gv) { gv = document.createElement("meta"); gv.name = "google-site-verification"; document.head.appendChild(gv); }
      gv.content = config.google_site_verification;
    }
    let ld = document.getElementById("nura-jsonld");
    if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "nura-jsonld"; document.head.appendChild(ld); }
    const origin = config.site_url || window.location.origin;
    ld.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": [
      { "@type": "Organization", name: "NŪRA", alternateName: ["Nura", "NURA", "Nūra cacao"], url: origin, logo: `${origin}/logo192.png`, email: config.company?.email || undefined, address: config.company?.address ? { "@type": "PostalAddress", streetAddress: config.company.address, addressLocality: config.company.zip_city, addressCountry: "CH" } : undefined, sameAs: ["instagram", "tiktok", "linkedin", "facebook", "youtube", "x", "pinterest"].map((k) => config.company?.[k]).filter(Boolean) },
      { "@type": "WebSite", name: "NŪRA", url: origin, inLanguage: [lang] },
      ...products.filter((p) => p.buyable).map((p) => ({ "@type": "Product", name: p.name?.[lang] || p.name?.fr, description: p.subtitle?.[lang] || p.subtitle?.fr, image: p.image?.startsWith("http") ? p.image : `${origin}${p.image}`, url: `${origin}/produit/${p.id}`, brand: { "@type": "Brand", name: "NŪRA" }, offers: { "@type": "Offer", price: p.price, priceCurrency: "CHF", availability: p.effective_status === "preorder" ? "https://schema.org/PreOrder" : "https://schema.org/InStock", url: `${origin}/produit/${p.id}` } })),
    ] });
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
    canon.href = origin + window.location.pathname;
    setProp("og:url", origin + window.location.pathname);
  }, [config.seo, config.google_site_verification, config.company, config.site_url, lang, products, location.pathname]);
  useEffect(() => { localStorage.setItem("nura_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { promo ? localStorage.setItem("nura_promo", promo) : localStorage.removeItem("nura_promo"); promoInfo ? localStorage.setItem("nura_promo_info", JSON.stringify(promoInfo)) : localStorage.removeItem("nura_promo_info"); }, [promo, promoInfo]);
  // validates a code against the backend and stores it; returns the coupon or throws
  const applyPromoCode = async (code, subtotal) => {
    const r = await axios.get(`${API}/promo/${encodeURIComponent(code.trim().toUpperCase())}`, { params: { subtotal } });
    setPromoState(r.data.code); setPromoInfo(r.data); return r.data;
  };
  const setPromo = (code) => { if (!code) { setPromoState(null); setPromoInfo(null); } else setPromoState(code); };
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const setLang = (l) => { localStorage.setItem("nura_lang", l); setLangState(l); };
  const t = useMemo(() => applyOverrides(T[lang], overrides[lang] || {}), [lang, overrides]);
  const img = (key) => overrides.images?.[key] || IMAGES[key];
  const setConsent = (c) => {
    const val = { essential: true, analytics: !!c.analytics, chat: !!c.chat, at: new Date().toISOString() };
    sessionStorage.setItem("nura_consent", JSON.stringify(val)); localStorage.removeItem("nura_consent"); setConsentState(val);
    axios.post(`${API}/consent`, { visitor_id: persistentVisitorId, analytics: val.analytics, chat: val.chat, lang }).catch(() => {});
  };
  const analyticsOn = !config.cookie_banner_enabled || !!consent?.analytics;

  const addToCart = (id, qty = 1) => {
    setTimeout(() => track("add_to_cart", { product_id: id }), 50);
    setCart((prev) => {
      const found = prev.find((i) => i.id === id);
      if (found) return prev.map((i) => (i.id === id ? { ...i, qty: i.qty + qty } : i));
      return [...prev, { id, qty }];
    });
  };

  const updateQty = (id, delta) => {
    setCart((prev) =>
      prev
        .map((i) => (i.id === id ? { ...i, qty: i.qty + delta } : i))
        .filter((i) => i.qty > 0)
    );
  };

  const removeItem = (id) => setCart((prev) => prev.filter((i) => i.id !== id));
  const clearCart = () => { setCart([]); setPromo(null); };

  const totals = useMemo(() => {
    const items = cart.map((c) => ({ ...PRODUCTS.find((p) => p.id === c.id), qty: c.qty })).filter((i) => i.id);
    const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
    const c = promoInfo && promo === promoInfo.code && config.promo_enabled && subtotal >= (promoInfo.min_subtotal || 0) ? promoInfo : null;
    const calc = (ship) => !c ? 0 : c.type === "percent" ? Math.round((subtotal + ship) * c.value) / 100 : Math.min(c.value, subtotal + ship);
    const baseDiscount = calc(0);
    const shipping = subtotal === 0 || c?.free_shipping || subtotal - baseDiscount >= config.free_shipping_threshold ? 0 : config.shipping_cost;
    const discount = Math.round(calc(shipping) * 100) / 100;
    const total = Math.round((subtotal + shipping - discount) * 100) / 100;
    const count = items.reduce((s, i) => s + i.qty, 0);
    return { items, subtotal, discount, shipping, total, count };
  }, [cart, promo, promoInfo, config, products]); // eslint-disable-line react-hooks/exhaustive-deps -- PRODUCTS is hydrated in place when `products` loads

  // audience measurement is always on (anonymous session id, no IP kept); consent unlocks the persistent id + IP retention
  const visitorId = analyticsOn ? persistentVisitorId : sessionVisitorId;
  const trackCtx = useRef({});
  trackCtx.current = { visitorId, analyticsOn, count: totals.count, total: totals.total, lang };
  const track = useCallback((event, meta) => {
    const c = trackCtx.current;
    axios.post(`${API}/track`, { visitor_id: c.visitorId, event, consent: c.analyticsOn, path: window.location.pathname, cart_count: c.count, cart_total: c.total, lang: c.lang,
      referrer: document.referrer.slice(0, 200), device: /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop", meta: meta || null }).catch(() => {});
  }, []);
  useEffect(() => { track("page_view"); }, [location.pathname, analyticsOn, track]);
  useEffect(() => { const iv = setInterval(() => document.visibilityState === "visible" && track("heartbeat"), 20000); return () => clearInterval(iv); }, [track]);
  useEffect(() => { if (cartOpen) track("cart_open"); }, [cartOpen, track]);

  const value = { lang, setLang, t, img, cart, addToCart, updateQty, removeItem, clearCart, cartOpen, setCartOpen, promo, setPromo, promoInfo, applyPromoCode, totals, stock, config, pages, products, productsLoaded, track, visitorId, consent, setConsent, analyticsOn };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export const useStore = () => useContext(StoreContext);
